import {
  AnsweringClient,
  GetManagedInstrumentsSearchParams,
  InstrumentDiscount,
} from "../clients/answeringClient";

export type { InstrumentDiscount } from "../clients/answeringClient";

export interface IInstrument {
  id: number;
  title: string;
  description: string;
  priceInBrlCents: number;
  quantity: number;
  discountRules: InstrumentDiscount[];
}

export interface ManagedInstrumentsControllerDelegate {
  didGetInstruments(instruments: IInstrument[]): void;
  didGetInstrumentDiscounts(
    instrumentId: number,
    discounts: InstrumentDiscount[]
  ): void;
  didFailToGetInstrumentDiscounts?(instrumentId: number): void;
  didUpdateInstrumentDiscounts?(
    instrumentId: number,
    discounts: InstrumentDiscount[]
  ): void;
  didFailToUpdateInstrumentDiscounts?(instrumentId: number): void;
  filter({
    page,
    total,
    filter,
  }: {
    page: number;
    total: number;
    filter: string;
  }): void;
  applicationShouldLogout(): void;
  genericError(): void;
}

export class ManagedInstrumentsController {
  constructor(
    private delegate: ManagedInstrumentsControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  public async getManagedInstruments(
    accessToken: string,
    options?: GetManagedInstrumentsSearchParams
  ) {
    try {
      const response = await this.answeringClient.getManagedInstrumentsSearch(
        accessToken,
        options
      );
      const data = response.data.data.map((instrument) => ({
        title: instrument.title,
        description: instrument.description,
        priceInBrlCents: instrument.priceInBrlCents,
        quantity: instrument.quantity,
        id: instrument.id,
        discountRules:
          instrument.discountRules?.map((rule) => ({
            id: rule.id,
            minQty: rule.minQty,
            maxQty: rule.maxQty,
            discountPct: rule.discountPct,
            unitPriceOverrideCents: rule.unitPriceOverrideCents ?? null,
            discountedUnitPriceCents: rule.discountedUnitPriceCents ?? null,
          })) ?? [],
      })) as IInstrument[];
      this.delegate.didGetInstruments(data);
      this.delegate.filter({
        page: response.data.page,
        total: response.data.total,
        filter: response.data.filter || "",
      });
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.getManagedInstruments(newTokens.accessToken);
        return;
      }
      this.delegate.genericError();
    }
  }

  public async getInstrumentDiscounts(
    accessToken: string,
    instrumentId: number
  ) {
    try {
      const response = await this.answeringClient.getInstrumentDiscounts(
        accessToken,
        instrumentId
      );

      const discounts = response.data.map(
        (discount): InstrumentDiscount => ({
          id: discount.id,
          minQty: discount.minQty,
          maxQty: discount.maxQty,
          discountPct: discount.discountPct,
          unitPriceOverrideCents: discount.unitPriceOverrideCents ?? null,
          discountedUnitPriceCents: discount.discountedUnitPriceCents ?? null,
        })
      );

      this.delegate.didGetInstrumentDiscounts(instrumentId, discounts);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.getInstrumentDiscounts(newTokens.accessToken, instrumentId);
        return;
      }
      if (this.delegate.didFailToGetInstrumentDiscounts) {
        this.delegate.didFailToGetInstrumentDiscounts(instrumentId);
      }
      this.delegate.genericError();
    }
  }

  public async updateInstrumentDiscounts(
    accessToken: string,
    instrumentId: number,
    discounts: InstrumentDiscount[]
  ) {
    try {
      await this.answeringClient.updateInstrumentDiscounts(
        accessToken,
        instrumentId,
        discounts
      );

      if (this.delegate.didUpdateInstrumentDiscounts) {
        this.delegate.didUpdateInstrumentDiscounts(instrumentId, discounts);
      }
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.updateInstrumentDiscounts(newTokens.accessToken, instrumentId, discounts);
        return;
      }
      if (this.delegate.didFailToUpdateInstrumentDiscounts) {
        this.delegate.didFailToUpdateInstrumentDiscounts(instrumentId);
      }
      this.delegate.genericError();
    }
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }
}
